import { useEffect } from "react";

// Fades up, as they scroll into view, the blocks of whatever page is rendered
// in `ref` (the <main>), so every page animates without wrapping each section
// in <Reveal>. A block is an element no taller than most of a screen; taller
// ones are split into their children. Blocks already in a <Reveal>, carousels
// and the loading skeleton are left alone. Styles live in GlobalStyles.
const KEEP_WHOLE =
  ".carousel-root, .rec-carousel-wrapper, form, table, iframe, img, p, li, h1, h2, h3, h4";
const SKIP = "[data-reveal], [aria-busy='true']";

const useAutoReveal = (ref) => {
  useEffect(() => {
    const root = ref.current;
    if (
      !root ||
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const seen = new WeakSet();

    const io = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top ||
              a.boundingClientRect.left - b.boundingClientRect.left
          )
          .forEach(({ target }, i) => {
            // Blocks arriving together cascade.
            const delay = Math.min(i, 4) * 90;
            io.unobserve(target);
            target.style.setProperty("--ar-delay", `${delay}ms`);
            target.setAttribute("data-ar", "in");
            // Then hand the block back to its own styles (hover transforms…).
            setTimeout(() => {
              target.removeAttribute("data-ar");
              target.style.removeProperty("--ar-delay");
            }, delay + 900);
          });
      },
      { rootMargin: "0px 0px -8% 0px" }
    );

    const walk = (el) => {
      const limit = Math.min(window.innerHeight * 0.7, 560);
      Array.from(el.children).forEach((child) => {
        if (seen.has(child) || child.matches(SKIP)) return;
        const { display, position } = getComputedStyle(child);
        if (display === "inline" || position === "absolute" || position === "fixed") {
          return;
        }
        const { height } = child.getBoundingClientRect();
        if (!height) return;
        if (
          child.querySelector(SKIP) ||
          (height > limit && child.children.length && !child.matches(KEEP_WHOLE))
        ) {
          walk(child);
        } else {
          seen.add(child);
          child.setAttribute("data-ar", "hide");
          io.observe(child);
        }
      });
    };

    // Runs before the browser paints new content, so it never flashes in
    // before hiding.
    const mo = new MutationObserver(() => walk(root));
    mo.observe(root, { childList: true, subtree: true });
    walk(root);

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [ref]);
};

export default useAutoReveal;
